// =============================================================================
// DOMAIN PRIMITIVES — Schema Types
// =============================================================================

export type Archetype = 'TRANSFORM' | 'TRANSFER' | 'MEASURE' | 'COMPLEX';

export type Unit =
  | 'g' | 'kg' | 'mg' | 'µg'
  | 'L' | 'mL' | 'µL' | 'nL'
  | 'count';

export type QuantityType = 'Volume' | 'Weight' | 'Count';

export interface Quantity {
  value: { amount: number; units: Unit };
  type: QuantityType;
}

export interface Constituent {
  name: string;
  weight: number; // relative weight 0..1
}

export const SYMBOLIC_QUANTITY_VALUES = [
  'supernatant',
  'pellet',
  'filtrate',
  'residue',
  'aliquot',
  'remainder',
  'entire',
] as const;

export type KnownSymbolicQuantity = (typeof SYMBOLIC_QUANTITY_VALUES)[number];
export type SymbolicQuantity = KnownSymbolicQuantity | (string & {});

// =============================================================================
// CONTAINER
// =============================================================================

export type ContainerType =
  | 'Tube'
  | 'PCR Tube'
  | 'Microcentrifuge Tube'
  | 'Falcon Tube'
  | 'Well Plate'
  | 'Flask'
  | 'Beaker'
  | 'Vial'
  | 'Bottle'
  | 'Other';

export interface PlateAddress {
  name: string;
  row: string;
  column: string;
}

export interface Container {
  id: string;
  name: string;
  containerType: ContainerType;
  volume?: { amount: number; unit: Unit };
  make?: string;
  copyName: boolean;
  rows?: number;
  columns?: number;
  wells?: number;
  wellVolume?: { amount: number; units: Unit };
  wellAddresses?: PlateAddress[];
}

// =============================================================================
// SOURCE MATERIAL (merge tracking)
// =============================================================================

export interface SourceMaterial {
  sourceVertexId: string;
  name: string;
  container: Container;
  notes: string | null;
}

// =============================================================================
// COMPOSITION (lineage tracking per material node)
// =============================================================================

export interface CompositionConstituent {
  materialName: string;
  containerName?: string;
  /** vertexId of the originating material node in the same graph */
  sourceNodeId: string;
  quantityContributed?: Quantity;
}

export interface DirectContribution {
  fromNodeId: string;
  fromNodeName?: string;
  contributionType: 'transfer' | 'pairEdge' | 'initial' | 'complex_remainder';
  quantity?: Quantity;
  /** vertexId of the operation that caused this contribution */
  stepId?: string;
  stepOrder?: number;
}

export interface Composition {
  finalQuantity?: Quantity;
  constituents?: CompositionConstituent[];
  directContributions?: DirectContribution[];
}

/**
 * Structural lineage of a material, derived from the graph topology.
 * Owned/computed by the v2 backend on save and hydrated read-only into the
 * editor; mirrors the `lineage` sub-document on the VertexV2 material schema.
 */
export interface MaterialLineage {
  /** Operation vertexId that produced this material (undefined for inputs). */
  producedByVertexId?: string;
  /** Snapshot of the producing operation's name. */
  producedByOperation?: string;
  /** Immediate upstream material vertexIds (inputs of the producing op). */
  derivedFromMaterialIds?: string[];
  /** All entry-point (isInput) ancestor material vertexIds. */
  rootMaterialIds?: string[];
  /** Hops from the nearest isInput ancestor (0 for inputs). */
  depth?: number;
  /** Reproducibility fingerprint of the lineage chain. */
  hash?: string | null;
}

// =============================================================================
// MATERIAL NODE SPEC
// =============================================================================

export interface MaterialNodeSpec {
  id?: string;
  vertexId?: string;
  kind?: 'material';
  name: string;
  container: Container;
  constituents: Constituent[];
  quantity: Quantity | null;
  isEmpty?: boolean;
  symbolicQuantity?: SymbolicQuantity;
  sourceMaterials?: SourceMaterial[];
  composition?: Composition;
  /** Structural lineage, derived backend-side from topology; read-only in editor. */
  lineage?: MaterialLineage;
  stateFlags?: string[];
  notes?: string | null;
  batchNode?: boolean;
  dependentNode?: boolean;
  mergedNode?: boolean;
  isReminder?: boolean;
  cloneNumber?: number;
  legacyMaterialName?: string;
  position?: { x: number; y: number };
  createdAt?: string;
}

// =============================================================================
// TRANSFER STRUCTURES
// =============================================================================

export interface TransferRow {
  id: string;
  /** Source material before the transfer (pre-state). */
  sourceMaterialId?: string;
  /** Destination material before the transfer (pre-state). */
  destinationMaterialId: string;
  /** Remainder material — source after the transfer (post-state). */
  sourcePostMaterialId?: string;
  /** Merged material — destination after the transfer (post-state). */
  destinationPostMaterialId?: string;
  quantity?: Quantity;
  symbolic?: SymbolicQuantity;
}

export interface TransferOptions {
  quantityType: QuantityType;
  transferEntireVolume: boolean;
  sameVolume: boolean;
  advancedOptionsEnabled: boolean;
}

// =============================================================================
// COMPLEX STRUCTURES
// =============================================================================

/**
 * A weighted link from one input material to one output material.
 * Fraction ∈ (0, 1] describes what share of the input flows to the output.
 */
export interface IOMapping {
  inputMaterialId: string;
  outputMaterialId: string;
  fraction: number;
}

/**
 * Runtime options stored on a COMPLEX operation node.
 * The actual topology is captured by FlowEdges; this stores the
 * quantitative IO mapping that edges alone cannot express.
 */
export interface ComplexOptions {
  ioMapping?: IOMapping[];
}

// ─── Complex Operation Slot Types ───────────────────────────────────────────
// Used by ComplexDetailsPanel, DonationGraphView, InputSlotPicker, OutputSlotsList.

export interface ComplexInputSlot {
  id: string;
  label: string;
  isPrimary?: boolean;
  description?: string;
}

export interface ComplexOutputSlot {
  id: string;
  label: string;
  /** Maps input slot id → fraction (0..1) of that input contributed to this output. */
  donations: Record<string, number>;
  isData?: boolean;
  defaultQuantity?: Quantity;
}

export interface ComplexConfig {
  inputSlots: ComplexInputSlot[];
  outputSlots: ComplexOutputSlot[];
}

// =============================================================================
// CHECKPOINT & PARAMETER STRUCTURES
// =============================================================================

export type CheckpointFieldType =
  | 'text'
  | 'textNumber'
  | 'number'
  | 'date'
  | 'user'
  | 'dropdown'
  | 'linkEntity'
  | 'file'
  | 'units'
  | 'group'
  | 'range'
  | 'table'
  | 'gradientProgram';

export interface DropdownOption {
  id: string;
  label: string;
}

export interface ReferenceFile {
  fileName: string | null;
  fileUrl: string | null;
  /** Azure blob container name when known */
  bucket?: string | null;
  /** Original upload metadata (path, bucket, workspaceId, …) */
  raw?: {
    filePath?: string;
    bucket?: string;
    workspaceId?: string;
    [k: string]: unknown;
  };
}

export interface SpreadsheetFileRef {
  entityId: string | null;
  name: string | null;
  /** Spreadsheet version ID used for navigation / open */
  versionId?: string | null;
}

export interface LinkedEntity {
  entityId: string;
  versionId?: string;
  name: string;
  entityType: 'SPREADSHEETFILE_VERSION' | 'SPREADSHEETFILE';
}

export interface Checkpoint {
  id: string;
  checkpointName: string;
  fieldType: CheckpointFieldType;
  required: boolean;
  allowMultiple: boolean;
  allowSpecialCharacters: boolean;
  uniqueAcrossWorkspace: boolean;
  /** True when this checkpoint was added by the user at step-authoring time (not from the catalog template). */
  isCustom?: boolean;
  units?: string[];
  unitSource?: 'existing' | 'custom';
  unitCategory?: string;
  linkMultiple?: boolean;
  allowedEntityTypes?: string[];
  dropdown?: DropdownOption[];
  rangeMin?: number;
  rangeMax?: number;
  referenceFile?: ReferenceFile;
  referenceSpreadsheetFile?: SpreadsheetFileRef;
  linkedEntities?: LinkedEntity[];
  children?: Checkpoint[];
}

export interface Duration {
  isInfinity: boolean;
  value?: number;
  linkToPreviousStep?: boolean;
  prevStep?: string;
}

export interface RecommendedInstrumentRef {
  value: string;
  workspaceId: string;
}

// =============================================================================
// OPERATION NODE SPEC
// =============================================================================

export interface UserRef {
  _id: string;
  name: string;
  email?: string;
}

export interface StepApprovers {
  approverType: 'before' | 'after' | null;
  users: UserRef[];
}

export interface StepNotifications {
  notificationType: 'before' | 'after' | null;
  users: UserRef[];
}

export interface OperationNodeSpec {
  id?: string;
  vertexId?: string;
  kind?: 'operation';
  archetype: Archetype;
  operationTypeId?: string;
  displayName: string;
  comments?: string;
  parameters: Record<string, unknown>;
  hideParamsMap?: Record<string, boolean>;
  operationTemplate?: {
    operationTypeId?: string;
    hideParamsMap?: Record<string, boolean>;
    parameters?: Array<Record<string, unknown>>;
    translationTemplate?: string;
  };
  parametersSchemaChanged?: boolean;
  checkPoints: Checkpoint[];
  approvers?: StepApprovers;
  notifications?: StepNotifications;
  duration?: Duration;
  creationOrder?: number;
  executeOrder?: number;
  orderString?: string;
  groupId?: string;
  orderInGroup?: number;
  uiGroupId?: string;
  isRepeated?: boolean;
  transferRow?: TransferRow;
  transferOptions?: TransferOptions;
  complexOptions?: ComplexOptions;
  batchEdge?: boolean;
  dependentEdge?: boolean;
  pairEdge?: null;
  cloneNumber?: number;
  recommendedInstrument?: string | RecommendedInstrumentRef[];
  metaActionsTags?: string[];
  dataExpected?: boolean;
  conditions?: unknown[];
  notes?: string | null;
  yield?: { amount: number; isPercent?: boolean };
  position?: { x: number; y: number };
  createdAt?: string;
}

// =============================================================================
// FLOW EDGE SPEC
// =============================================================================

export interface FlowEdgeSpec {
  id?: string;
  edgeId?: string;
  kind?: 'flowEdge';
  sourceId: string;
  targetId: string;
  /** 'flow' = normal material→operation→material edge chain
   *  'graphsLink' = dashed edge linking endpoints of disconnected subgraphs
   *  Default: 'flow' for backward compatibility
   */
  edgeKind?: 'flow' | 'graphsLink';
  /**
   * Batch instance this edge belongs to (1-based, defaults to 1).
   * Batch clones share `edgeId`/`sourceId`/`targetId`, so this is the only
   * field that separates them — see `filterSnapshotByClone` in ./batches.
   */
  cloneNumber?: number;
}

// =============================================================================
// GROUP SPEC
// =============================================================================

export interface CycleRange {
  id: string;
  startIndex: number;
  endIndex: number;
  iterationCount: number;
  label?: string;
  /**
   * Computed on every structural graph change via CycleMembershipResolver.
   * Contains the IDs of all cells (operations + materials) on the induced
   * subgraph between the cycle's start and end cells.
   *
   * Persisted for serialization, but always recomputed on graph load.
   * Do NOT rely on stale values — call `resolveCycleMembers()` after any
   * cell/edge mutation.
   */
  memberCellIds?: string[];
}

export interface GroupSpec {
  id?: string;
  kind?: 'group';
  label: string;
  stepIds: string[];
  cycleRanges?: CycleRange[];
  isCollapsed?: boolean;
  notes?: string;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  createdAt?: string;
  /**
   * Set during load-time validation if group topology is invalid.
   * Presence of this field indicates the group is broken and needs manual repair.
   * Possible values: 'disconnected', 'gap', 'duplicate', 'missing-step', 'already-grouped'
   */
  invalidReason?: 'disconnected' | 'gap' | 'duplicate' | 'missing-step' | 'already-grouped';
  /** Batch instance this group belongs to (1-based, defaults to 1). See ./batches. */
  cloneNumber?: number;
}

// =============================================================================
// GRAPH SNAPSHOT
// =============================================================================

export interface GraphSnapshot {
  id?: string;
  materialNodes: MaterialNodeSpec[];
  operationNodes: OperationNodeSpec[];
  flowEdges: FlowEdgeSpec[];
  groups: GroupSpec[];
  schemaVersion?: string;
}

/**
 * Record-keyed (id → spec) view of the graph, as held in the editor store.
 * GraphSnapshot (arrays) remains the persistence/backend contract.
 */
export interface GraphRecords {
  materialNodes: Record<string, MaterialNodeSpec>;
  operationNodes: Record<string, OperationNodeSpec>;
  flowEdges: Record<string, FlowEdgeSpec>;
  groups: Record<string, GroupSpec>;
}

// =============================================================================
// OPERATION TYPE DEFINITION (Catalog)
// =============================================================================

export type ParameterFieldType =
  | 'text'
  | 'number'
  | 'textNumber'
  | 'dropdown'
  | 'group'
  | 'gradientProgram'
  | 'table'
  | 'date'
  | 'user'
  | 'file'
  | 'units'
  | 'range'
  | 'linkEntity';

export interface ParameterDefinition {
  id: string;
  parameterName: string;
  fieldType: ParameterFieldType;
  order: number;
  required: boolean;
  uniqueAcrossWorkspace: boolean;
  allowSpecialCharacters: boolean;
  /** When fieldType is 'group', controls whether multiple row instances are allowed. */
  allowMultiple?: boolean;
  parameterTags?: string[];
  dropDownList?: { id: string; label: string }[];
  rangeMin?: number | null;
  rangeMax?: number | null;
  tableColumns?: { id: string; header: string; required?: boolean }[];
  rowCount?: number | null;
  pumpCount?: number | null;
  children?: ParameterDefinition[];
  safeKey?: string;
}

export interface OperationTypeDefinition {
  id: string;
  uid: string;
  operationName: string;

  /** Archetype from the backend operationType field. null for legacy ops without archetype. */
  operationType: Archetype | null;

  /** The v5 subType field (transformation/measurement/transfer). Mapped to Archetype in the filter. */
  subType?: string;

  /**
   * How this operation renders in the visual graph.
   * 'edge'  — collapsed into a labeled edge between MaterialNodes (default for simple ops).
   * 'node'  — rendered as a visible node with flow edges (default for COMPLEX).
   * Set at catalog level (design-time), not runtime.
   */
  displayMode?: 'edge' | 'node';

  display: boolean;
  isArchived: boolean;
  isSoftDeleted: boolean;
  isLocked: boolean;
  versionNumber: number;
  isCurrentVersion: boolean;
  versions: string[];

  durationRequired: boolean;
  durationAllow: boolean;
  canEditCheckpoints: boolean;

  translationTemplate: string;

  parameters: ParameterDefinition[];
  checkpoints: Checkpoint[];

  operationTypeConfig: {
    allowYield: boolean;
    allowMultipleInputs?: boolean;
    allowMultipleOutputs?: boolean;
    allowIOMapping?: boolean;
  };

  workspaceId: string;
  addedBy: { id: string; email: string; name: string; profilePhoto?: string | null };
  createdOn: string;
  updatedOn: string;
}
